// src/utils/capturaMapa.js
// Capturas del mapa para el PDF: la vista general y varios acercamientos
// (uno por lote analizado o, si hay un solo lote, por zonas del análisis).
// Lo usan el botón PDF y el PDF automático del análisis en segundo plano.
import * as turf from '@turf/turf';

const MAX_ACERCAMIENTOS_LOTE = 6;
const MAX_ACERCAMIENTOS_ZONA = 4;
const MAX_ACERCAMIENTOS = 8;
const MAX_TRAMOS_POR_OBJETIVO = 3;
const ESPERA_IDLE_MS = 10000;
// Tiempo total para todos los acercamientos: si el satélite tarda en cargar, el
// PDF sale con los que alcanzaron en vez de dejar el botón girando minutos.
const TIEMPO_MAXIMO_ACERCAMIENTOS_MS = 45000;

const esperar = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const obtenerMapa = (mapRef) => {
    const actual = mapRef?.current;
    if (!actual) return null;
    return typeof actual.getMap === 'function' ? actual.getMap() : actual;
};

// Espera a que el mapa termine de pintar (teselas, fuentes y capas que React
// monta al cambiar el zoom, como el detalle del VRA).
export const esperarMapaQuieto = async (map, { minimoMs = 400, maximoMs = ESPERA_IDLE_MS } = {}) => {
    await esperar(minimoMs);
    if (!map) return;
    const listo = () => {
        try {
            return map.loaded() && map.areTilesLoaded();
        } catch {
            return true;
        }
    };
    if (listo()) return;
    await new Promise((resolve) => {
        const timer = setTimeout(resolve, maximoMs);
        map.once('idle', () => {
            clearTimeout(timer);
            resolve();
        });
    });
};

// El mapa del VRA no usa preserveDrawingBuffer: se lee el canvas justo
// después de un render para no obtener una imagen en blanco.
export const capturarCanvas = (map, { formato = 'image/png', calidad } = {}) => new Promise((resolve, reject) => {
    if (!map) {
        reject(new Error('Map reference not available'));
        return;
    }
    let resuelto = false;
    const leer = () => {
        if (resuelto) return;
        resuelto = true;
        try {
            resolve(map.getCanvas().toDataURL(formato, calidad));
        } catch (error) {
            reject(error);
        }
    };
    map.once('render', leer);
    map.triggerRepaint();
    setTimeout(leer, 1500);
});

const esCapaDeLotes = (layer) => {
    const id = String(layer?.id || '');
    return id === 'polygons-data-layer' || id.startsWith('lotes-geojson') || id.includes('lote');
};

// Capas del análisis: GeoJSON propias (no el mapa base ni los lotes).
export const capasDelAnalisis = (map) => {
    try {
        const style = map.getStyle();
        const sources = style?.sources || {};
        return (style?.layers || [])
            .filter((layer) => layer.source && sources[layer.source]?.type === 'geojson')
            .filter((layer) => !esCapaDeLotes(layer))
            .filter((layer) => map.getLayoutProperty(layer.id, 'visibility') !== 'none')
            .map((layer) => layer.id);
    } catch {
        return [];
    }
};

const featuresVisibles = (map, caja = null) => {
    const layers = capasDelAnalisis(map);
    if (layers.length === 0) return [];
    try {
        return caja ? map.queryRenderedFeatures(caja, { layers }) : map.queryRenderedFeatures({ layers });
    } catch {
        return [];
    }
};

const bboxDeFeatures = (features) => {
    let bbox = null;
    features.forEach((feature) => {
        if (!feature?.geometry) return;
        try {
            const [minX, minY, maxX, maxY] = turf.bbox(feature);
            if (![minX, minY, maxX, maxY].every(Number.isFinite)) return;
            bbox = bbox
                ? [Math.min(bbox[0], minX), Math.min(bbox[1], minY), Math.max(bbox[2], maxX), Math.max(bbox[3], maxY)]
                : [minX, minY, maxX, maxY];
        } catch {
            // geometría rara: se ignora
        }
    });
    return bbox;
};

// Último recurso: extensión de las fuentes GeoJSON del análisis que traen
// los datos en memoria (no sirve para las capas remotas por URL).
const bboxDeFuentes = (map) => {
    try {
        const style = map.getStyle();
        const features = [];
        Object.entries(style?.sources || {}).forEach(([id, source]) => {
            if (source?.type !== 'geojson' || id.includes('lote') || id === 'polygons-data') return;
            const data = map.getSource(id)?.serialize?.()?.data ?? source.data;
            if (Array.isArray(data?.features)) features.push(...data.features.slice(0, 5000));
            else if (data?.geometry) features.push(data);
        });
        return bboxDeFeatures(features);
    } catch {
        return null;
    }
};

const anchoVisible = (map) => {
    try {
        const b = map.getBounds();
        const km = turf.distance([b.getWest(), b.getCenter().lat], [b.getEast(), b.getCenter().lat], { units: 'kilometers' });
        return km >= 1 ? `ancho visible ≈ ${km.toFixed(1)} km` : `ancho visible ≈ ${Math.round(km * 1000)} m`;
    } catch {
        return '';
    }
};

const valorProp = (props, claves) => {
    const mapa = {};
    Object.keys(props || {}).forEach((k) => {
        mapa[String(k).toLowerCase().replace(/[^a-z0-9]/g, '')] = props[k];
    });
    for (const clave of claves) {
        const v = mapa[clave.toLowerCase().replace(/[^a-z0-9]/g, '')];
        if (v !== undefined && v !== null && String(v).trim() !== '') return String(v).trim();
    }
    return '';
};

const nombreLote = (props) => {
    const lote = valorProp(props, ['LOTE N', 'LOTE_N', 'LOTE', 'NUM_LOTE', 'LOTE_NUM', 'LOTE 1', 'loteId', 'LOTE_ID', 'ID_LOTE']);
    const finca = valorProp(props, ['N. FINCA', 'N_FINCA', 'NOMBRE_FINCA', 'Nombre Finca', 'Nombre Fin', 'N_FINCAS', 'FINCA_NOMBRE']);
    return [lote ? `Lote ${lote}` : 'Lote', finca].filter(Boolean).join(' · ');
};

// Un lote largo y angosto (o muy ancho) casi no se acerca al encuadrarlo
// completo: se parte en tramos con la forma de la pantalla para que cada
// imagen sí quede más cerca. Solo se dejan los tramos con datos encima.
const tramosSegunPantalla = (map, objetivo, ancho, alto) => {
    const [minX, minY, maxX, maxY] = objetivo.bbox;
    const sw = map.project([minX, minY]);
    const ne = map.project([maxX, maxY]);
    const anchoPx = Math.abs(ne.x - sw.x);
    const altoPx = Math.abs(sw.y - ne.y);
    if (anchoPx < 1 || altoPx < 1) return [objetivo];
    const aspectoPantalla = ancho / alto;
    const aspectoObjetivo = anchoPx / altoPx;
    const vertical = aspectoObjetivo < aspectoPantalla;
    const n = Math.min(MAX_TRAMOS_POR_OBJETIVO, Math.round(vertical ? aspectoPantalla / aspectoObjetivo : aspectoObjetivo / aspectoPantalla));
    if (n < 2) return [objetivo];
    const nombres = vertical
        ? (n === 2 ? ['norte', 'sur'] : ['norte', 'centro', 'sur'])
        : (n === 2 ? ['oeste', 'este'] : ['oeste', 'centro', 'este']);
    const tramos = [];
    for (let i = 0; i < n; i += 1) {
        const bbox = vertical
            ? [minX, maxY - ((i + 1) * (maxY - minY)) / n, maxX, maxY - (i * (maxY - minY)) / n]
            : [minX + (i * (maxX - minX)) / n, minY, minX + ((i + 1) * (maxX - minX)) / n, maxY];
        const a = map.project([bbox[0], bbox[1]]);
        const b = map.project([bbox[2], bbox[3]]);
        const caja = [[Math.min(a.x, b.x), Math.min(a.y, b.y)], [Math.max(a.x, b.x), Math.max(a.y, b.y)]];
        if (featuresVisibles(map, caja).length > 0) {
            tramos.push({ ...objetivo, bbox, titulo: `${objetivo.titulo} (${nombres[i]})` });
        }
    }
    return tramos.length ? tramos : [objetivo];
};

const encuadrar = async (map, bbox, padding = 40, maximoMs = ESPERA_IDLE_MS) => {
    map.fitBounds([[bbox[0], bbox[1]], [bbox[2], bbox[3]]], { padding, animate: false, maxZoom: 19 });
    await esperarMapaQuieto(map, { minimoMs: 600, maximoMs });
};

/**
 * Encuadra el mapa en lo analizado (no en todos los lotes de la empresa).
 * Devuelve false si no encontró capas del análisis dibujadas.
 */
export const encuadrarAnalisis = async (mapRef, lotes = []) => {
    const map = obtenerMapa(mapRef);
    if (!map) return false;
    let features = featuresVisibles(map);
    if (features.length === 0 && lotes.length > 0) {
        const bboxLotes = bboxDeFeatures(lotes);
        if (bboxLotes) {
            await encuadrar(map, bboxLotes);
            features = featuresVisibles(map);
        }
    }
    let bbox = bboxDeFeatures(features) || bboxDeFuentes(map);
    if (!bbox) return false;
    await encuadrar(map, bbox);
    // Segunda pasada: con más zoom las geometrías dibujadas son más exactas.
    bbox = bboxDeFeatures(featuresVisibles(map)) || bbox;
    await encuadrar(map, bbox);
    return true;
};

/**
 * Captura acercamientos sin mover la vista del usuario: al terminar el mapa
 * vuelve a donde estaba. Devuelve [{ titulo, subtitulo, image }].
 */
// El PDF automático (sin nadie mirando) usa las esperas completas; el botón PDF
// pasa límites cortos para que el usuario no vea el botón girando un minuto.
export const capturarAcercamientos = async (
    mapRef,
    lotes = [],
    { onPaso, esperaMaximaMs = ESPERA_IDLE_MS, tiempoMaximoMs = TIEMPO_MAXIMO_ACERCAMIENTOS_MS } = {},
) => {
    const map = obtenerMapa(mapRef);
    if (!map) return [];

    const camara = {
        center: map.getCenter(),
        zoom: map.getZoom(),
        bearing: map.getBearing(),
        pitch: map.getPitch(),
    };
    const canvas = map.getCanvas();
    const ancho = canvas.clientWidth || canvas.width;
    const alto = canvas.clientHeight || canvas.height;

    const objetivos = [];
    try {
        // Lotes visibles en la vista general que tienen algo del análisis encima.
        const candidatos = (Array.isArray(lotes) ? lotes : [])
            .filter((lote) => lote?.geometry)
            .map((lote) => {
                let bbox = null;
                let area = 0;
                try {
                    bbox = turf.bbox(lote);
                    area = turf.area(lote);
                } catch {
                    return null;
                }
                const sw = map.project([bbox[0], bbox[1]]);
                const ne = map.project([bbox[2], bbox[3]]);
                const caja = [
                    [Math.max(0, Math.min(sw.x, ne.x)), Math.max(0, Math.min(sw.y, ne.y))],
                    [Math.min(ancho, Math.max(sw.x, ne.x)), Math.min(alto, Math.max(sw.y, ne.y))],
                ];
                if (caja[1][0] - caja[0][0] < 2 || caja[1][1] - caja[0][1] < 2) return null;
                const conDatos = featuresVisibles(map, caja).length > 0;
                return conDatos ? { bbox, area, titulo: nombreLote(lote.properties) } : null;
            })
            .filter(Boolean)
            .sort((a, b) => b.area - a.area);

        if (candidatos.length >= 2) {
            candidatos.slice(0, MAX_ACERCAMIENTOS_LOTE).forEach((c) => objetivos.push(c));
        } else {
            // Un solo lote (o ninguno): acercamientos por zonas del análisis.
            if (candidatos.length === 1) objetivos.push(candidatos[0]);
            const bbox = bboxDeFeatures(featuresVisibles(map));
            if (bbox) {
                const [minX, minY, maxX, maxY] = bbox;
                const midX = (minX + maxX) / 2;
                const midY = (minY + maxY) / 2;
                const zonas = [
                    { bbox: [minX, midY, midX, maxY], titulo: 'Zona noroeste' },
                    { bbox: [midX, midY, maxX, maxY], titulo: 'Zona noreste' },
                    { bbox: [minX, minY, midX, midY], titulo: 'Zona suroeste' },
                    { bbox: [midX, minY, maxX, midY], titulo: 'Zona sureste' },
                ];
                zonas.forEach((zona) => {
                    const sw = map.project([zona.bbox[0], zona.bbox[1]]);
                    const ne = map.project([zona.bbox[2], zona.bbox[3]]);
                    const caja = [[Math.min(sw.x, ne.x), Math.min(sw.y, ne.y)], [Math.max(sw.x, ne.x), Math.max(sw.y, ne.y)]];
                    if (objetivos.length < 1 + MAX_ACERCAMIENTOS_ZONA && featuresVisibles(map, caja).length > 0) {
                        objetivos.push(zona);
                    }
                });
            }
        }

        // Con la vista general aún puesta se decide si cada objetivo se parte.
        const finales = objetivos
            .flatMap((objetivo) => tramosSegunPantalla(map, objetivo, ancho, alto))
            .slice(0, MAX_ACERCAMIENTOS);

        const imagenes = [];
        const limite = Date.now() + tiempoMaximoMs;
        for (const [indice, objetivo] of finales.entries()) {
            if (Date.now() > limite) {
                console.warn(`[PDF] Acercamientos cortados por tiempo: ${imagenes.length}/${finales.length}`);
                break;
            }
            if (onPaso) onPaso(`Acercamiento ${indice + 1}/${finales.length}: ${objetivo.titulo}`);
            // eslint-disable-next-line no-await-in-loop
            await encuadrar(map, objetivo.bbox, 30, esperaMaximaMs);
            // eslint-disable-next-line no-await-in-loop
            // JPEG: los acercamientos son foto satelital y en PNG pesan varios MB cada uno.
            const image = await capturarCanvas(map, { formato: 'image/jpeg', calidad: 0.88 });
            imagenes.push({ titulo: objetivo.titulo, subtitulo: anchoVisible(map), image });
        }
        return imagenes;
    } catch (error) {
        console.warn('[PDF] No se pudieron capturar los acercamientos:', error);
        return [];
    } finally {
        try {
            map.jumpTo(camara);
            await esperarMapaQuieto(map, { minimoMs: 200, maximoMs: esperaMaximaMs });
        } catch {
            // el mapa pudo desmontarse
        }
    }
};
